import { useCallback, useEffect, useRef, useState } from "react";
import {
  resolvedWaveHeaderS3Key,
  waveHeaderS3KeyFromCardKey,
  type WaveCoverKeys,
  type WaveRecord,
} from "@vida/wave-core";
import { signedDownloadURL } from "../../services/waveBlobUpload";
import {
  needsAnyWaveCover,
  requestWaveCover,
  subscribeWaveCoverUpdates,
} from "../../services/waveCoverRequest";
import { WavePixelArtCover } from "./WavePixelArtCover";

export type WaveCoverBannerVariant = "card" | "list" | "headerBackground";

type Props = {
  wave: WaveRecord;
  variant?: WaveCoverBannerVariant;
  onCoverKeyChange?: (waveId: string, keys: WaveCoverKeys) => void;
};

export function WaveCoverBanner({
  wave,
  variant = "card",
  onCoverKeyChange,
}: Props) {
  const usesHeaderCover = variant === "headerBackground";
  const waveCoverKey = usesHeaderCover
    ? resolvedWaveHeaderS3Key(wave.coverImageHeaderS3Key, wave.coverImageS3Key)
    : wave.coverImageS3Key;

  const [resolvedKey, setResolvedKey] = useState<string | null>(
    waveCoverKey?.trim() || null,
  );
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [coverFetchGeneration, setCoverFetchGeneration] = useState(0);
  const autoRequestedRef = useRef(false);

  const effectiveKey = resolvedKey?.trim() || waveCoverKey?.trim() || null;

  const bumpCoverFetch = useCallback(() => {
    setCoverFetchGeneration((g) => g + 1);
  }, []);

  useEffect(() => {
    const key = usesHeaderCover
      ? resolvedWaveHeaderS3Key(wave.coverImageHeaderS3Key, wave.coverImageS3Key)
      : wave.coverImageS3Key?.trim() || null;
    setResolvedKey(key);
    if (key) bumpCoverFetch();
  }, [
    waveCoverKey,
    wave.coverImageS3Key,
    wave.coverImageHeaderS3Key,
    wave.waveId,
    bumpCoverFetch,
    usesHeaderCover,
  ]);

  useEffect(() => {
    return subscribeWaveCoverUpdates((waveId, keys) => {
      if (waveId !== wave.waveId) return;
      const key = usesHeaderCover
        ? resolvedWaveHeaderS3Key(keys.coverImageHeaderS3Key, keys.coverImageS3Key)
        : keys.coverImageS3Key;
      if (key?.trim()) {
        setResolvedKey(key.trim());
        bumpCoverFetch();
      }
    });
  }, [wave.waveId, bumpCoverFetch, usesHeaderCover]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!effectiveKey) {
        if (!cancelled) setCoverUrl(null);
        return;
      }
      const candidates = headerDownloadCandidates(effectiveKey);
      for (const candidate of candidates) {
        try {
          const url = await signedDownloadURL(candidate, wave.waveId, true);
          if (!cancelled) {
            setResolvedKey(candidate);
            setCoverUrl(url);
          }
          return;
        } catch {
          /* try next candidate */
        }
      }
      if (!cancelled) setCoverUrl(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveKey, wave.waveId, coverFetchGeneration, wave.coverImageS3Key, usesHeaderCover]);

  function headerDownloadCandidates(primaryKey: string): string[] {
    const keys = [primaryKey];
    if (usesHeaderCover) {
      const derived = waveHeaderS3KeyFromCardKey(wave.coverImageS3Key);
      if (derived && derived !== primaryKey) keys.push(derived);
    }
    return keys;
  }

  const runCoverRequest = useCallback(
    async (force: boolean) => {
      if (isRequesting) return;
      setIsRequesting(true);
      try {
        const outcome = await requestWaveCover(wave, force, (keys) => {
          const key = usesHeaderCover
            ? resolvedWaveHeaderS3Key(keys.coverImageHeaderS3Key, keys.coverImageS3Key)
            : keys.coverImageS3Key;
          if (key) {
            setResolvedKey(key);
            bumpCoverFetch();
          }
          onCoverKeyChange?.(wave.waveId, keys);
        });
        if (outcome.coverImageS3Key || outcome.coverImageHeaderS3Key) {
          const key = usesHeaderCover
            ? resolvedWaveHeaderS3Key(
                outcome.coverImageHeaderS3Key,
                outcome.coverImageS3Key,
              )
            : outcome.coverImageS3Key;
          if (key) {
            setResolvedKey(key);
            bumpCoverFetch();
          }
          onCoverKeyChange?.(wave.waveId, {
            coverImageS3Key: outcome.coverImageS3Key ?? null,
            coverImageHeaderS3Key: outcome.coverImageHeaderS3Key ?? null,
          });
        }
      } finally {
        setIsRequesting(false);
      }
    },
    [isRequesting, bumpCoverFetch, onCoverKeyChange, wave],
  );

  useEffect(() => {
    autoRequestedRef.current = false;
  }, [wave.waveId]);

  useEffect(() => {
    if (!needsAnyWaveCover(wave) || autoRequestedRef.current) return;
    autoRequestedRef.current = true;
    void runCoverRequest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto once per wave when cover missing
  }, [wave.waveId, wave.coverImageS3Key, wave.coverImageHeaderS3Key]);

  const className =
    variant === "list"
      ? "wave-cover-thumb wave-cover-thumb-list"
      : variant === "headerBackground"
        ? "wave-cover-header-bg"
        : "wave-cover-thumb";

  return (
    <div className={className}>
      {coverUrl ? (
        <WavePixelArtCover src={coverUrl} alt="" smooth={usesHeaderCover} />
      ) : (
        <div className="wave-cover-placeholder" aria-hidden />
      )}
      {!usesHeaderCover && (
        <div className="wave-cover-actions">
          <button
            type="button"
            className="link-btn"
            disabled={isRequesting}
            onClick={(e) => {
              e.stopPropagation();
              void runCoverRequest(!needsAnyWaveCover(wave));
            }}
          >
            {needsAnyWaveCover(wave) ? "Genera cover" : "Rigenera"}
          </button>
        </div>
      )}
    </div>
  );
}
